import { InterruptOutcome, PostLoginInterrupt } from './post-login-interrupt.model';
import {
  orderInterrupts,
  queueSignature,
  removeInterrupt,
  upsertInterrupt,
} from './interrupt-queue.util';
import { NULL_SETTLED_STORE, SettledStore } from './interrupt-settled-store';

/**
 * INC-327 — the queue's state machine, with no Angular and no `NgbModal` in it.
 *
 * The parts of this Feature with a compliance consequence are the ordering rule
 * (compliance ahead of dismissible) and the guarantee that an item settles EXACTLY
 * ONCE — the login call site's `onDefer` runs the post-login navigation, so a second
 * firing would navigate twice. Both live here rather than tangled up with modal
 * plumbing, so they can be reasoned about and tested directly.
 *
 * Note what this class deliberately cannot do: it has no way to change an investor's
 * gate status. A gate is a server-side fact; taking its popup off the screen removes a
 * reminder and nothing else. That is what stops the queue becoming a way past re-KYC.
 */
export class InterruptQueue {
  private items: PostLoginInterrupt[] = [];

  /** Resolved or deferred in this session — callbacks run once, and it will not re-queue. */
  private readonly settledKeys: Set<string>;

  /**
   * INC-329: the settled set is now hydrated from (and written through to) a session-scoped
   * store. Before this story the flag arrived once on the login response, so an in-memory
   * set was enough; now it is read from the profile on every app init, and without
   * persistence a flagged investor would be re-nudged on every page refresh.
   *
   * The default store keeps nothing, which is exactly the old behaviour — see
   * {@link SettledStore} for why session scope (and not local) is the right boundary.
   */
  constructor(private readonly store: SettledStore = NULL_SETTLED_STORE) {
    this.settledKeys = new Set(store.load());
  }

  /**
   * Register an interrupt, replacing any already queued under the same key.
   *
   * @returns whether the queue changed — the caller uses this to decide about
   *          re-presenting.
   */
  enqueue(interrupt: PostLoginInterrupt): boolean {
    // Already dealt with this session: the investor said "later" and meant it. The
    // server predicate raises it again on the next login.
    if (this.settledKeys.has(interrupt.key)) {
      return false;
    }
    const before = this.signature;
    this.items = upsertInterrupt(this.items, interrupt);
    return this.signature !== before;
  }

  /**
   * Take an item out and run its callback, once.
   *
   * @returns whether anything was settled.
   */
  settle(key: string, outcome: InterruptOutcome): boolean {
    const interrupt = this.items.find((queued) => queued.key === key);
    if (!interrupt || this.settledKeys.has(key)) {
      return false;
    }
    this.settledKeys.add(key);
    this.store.save([...this.settledKeys]);
    this.items = removeInterrupt(this.items, key);

    if (outcome === 'resolve') {
      interrupt.onResolve?.();
    } else {
      interrupt.onDefer?.();
    }
    return true;
  }

  /** Defer everything still queued — e.g. the frame was closed with items left on it. */
  deferAll(): void {
    for (const interrupt of [...this.items]) {
      this.settle(interrupt.key, 'defer');
    }
  }

  /**
   * Forget everything, running no callback. For a session that ended underneath us, and —
   * INC-329 — for a NEW login, which must re-raise whatever was put off last time. Session
   * storage alone would not do that: logging out and back in stays inside one tab.
   */
  clear(): void {
    this.items = [];
    this.settledKeys.clear();
    this.store.clear();
  }

  /** In the order the investor will meet them: compliance first, then by arrival. */
  get pending(): PostLoginInterrupt[] {
    return orderInterrupts(this.items);
  }

  get size(): number {
    return this.items.length;
  }

  /** The highest-ranked gate still queued, if any. Drives INC-328's persistent note. */
  get gate(): PostLoginInterrupt | undefined {
    return this.pending.find((interrupt) => interrupt.kind === 'gate');
  }

  /** Identity of the current contents, for "is this already what's on screen?". */
  get signature(): string {
    return queueSignature(this.items);
  }

  hasSettled(key: string): boolean {
    return this.settledKeys.has(key);
  }
}
